import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "./ConfirmDialog";

// Normaliza prefijos: el backend puede devolver string JSON o array
function parsePrefijos(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.length > 0) {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return [];
}

async function apiFetch(path, opts = {}) {
  const method = opts.method || "GET";
  const token = localStorage.getItem("token");
  const fetchOpts = {
    method,
    headers: {
      "Authorization": `Bearer ${token || ""}`,
      "Content-Type": "application/json",
    },
  };
  if (opts.body) fetchOpts.body = opts.body;
  const res = await fetch(`/v1${path}`, fetchOpts);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

// ─── Toggle switch ──────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <label style={{ display:"inline-flex", alignItems:"center", cursor: disabled ? "not-allowed" : "pointer", gap:6 }}>
      <span style={{
        position:"relative", display:"inline-block", width:36, height:20,
      }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          style={{ opacity:0, width:0, height:0, position:"absolute" }}/>
        <span style={{
          position:"absolute", cursor: disabled ? "not-allowed" : "pointer",
          top:0, left:0, right:0, bottom:0, borderRadius:10,
          background: checked ? "var(--green,#22c55e)" : "var(--border,#374151)",
          transition:"background .2s",
        }}>
          <span style={{
            position:"absolute", content:"", height:14, width:14,
            left: checked ? 19 : 3, top:3, borderRadius:"50%",
            background:"#fff", transition:"left .2s",
          }}/>
        </span>
      </span>
    </label>
  );
}

// ─── Modal de edición de permisos de un ANI ──────────────────────────────────
function EditModal({ ani, permisos, categories, onSave, onClose }) {
  const [local, setLocal] = useState(() => {
    const m = {};
    permisos.forEach(p => { m[p.categoria] = p.permitido; });
    return m;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleSave() {
    setSaving(true);
    setErr("");
    try {
      await apiFetch(`/permissions/ani/${encodeURIComponent(ani)}/bulk`, {
        method: "PUT",
        body: JSON.stringify(local),
      });
      onSave();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center"
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background:"var(--surface,#1e293b)", border:"1px solid var(--border,#374151)",
        borderRadius:12, padding:28, minWidth:420, maxWidth:520, width:"90%",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div>
            <h3 style={{ margin:0, fontSize:16, color:"var(--text,#f1f5f9)" }}>Permisos de marcado</h3>
            <div style={{ fontSize:12, color:"var(--muted,#94a3b8)", marginTop:2 }}>ANI: <code style={{ color:"var(--accent,#38bdf8)" }}>{ani}</code></div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--muted,#94a3b8)", fontSize:20, cursor:"pointer" }}>×</button>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {categories.map(cat => (
            <div key={cat.nombre} style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"10px 14px", background:"var(--bg,#0f172a)", borderRadius:8,
              border:"1px solid var(--border,#374151)"
            }}>
              <div>
                <div style={{ fontSize:14, color:"var(--text,#f1f5f9)", fontWeight:500 }}>{cat.label}</div>
                <div style={{ fontSize:11, color:"var(--muted,#94a3b8)", marginTop:2 }}>
                  Prefijos: {parsePrefijos(cat.prefijos).join(", ")}
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{
                  fontSize:11, padding:"2px 8px", borderRadius:4,
                  background: local[cat.nombre] ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)",
                  color: local[cat.nombre] ? "#22c55e" : "#ef4444",
                }}>
                  {local[cat.nombre] ? "Permitido" : "Bloqueado"}
                </span>
                <Toggle
                  checked={!!local[cat.nombre]}
                  onChange={v => setLocal(prev => ({ ...prev, [cat.nombre]: v }))}
                />
              </div>
            </div>
          ))}
        </div>

        {err && <div style={{ marginTop:12, color:"#ef4444", fontSize:12 }}>{err}</div>}

        <div style={{ display:"flex", gap:10, marginTop:20, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{
            padding:"8px 18px", borderRadius:8, border:"1px solid var(--border,#374151)",
            background:"none", color:"var(--muted,#94a3b8)", cursor:"pointer", fontSize:13
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding:"8px 18px", borderRadius:8, border:"none",
            background:"var(--accent,#38bdf8)", color:"#0f172a", fontWeight:600,
            cursor: saving ? "not-allowed" : "pointer", fontSize:13, opacity: saving ? .7 : 1
          }}>
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de edición de categoría (prefijos, label, default) ────────────────
function EditCategoryModal({ cat, onSave, onClose }) {
  const [label,     setLabel]   = useState(cat.label || '');
  const [prefStr,   setPrefStr] = useState(parsePrefijos(cat.prefijos).join(', '));
  const [defPerm,   setDefPerm] = useState(!!cat.permitido_default);
  const [saving,    setSaving]  = useState(false);
  const [err,       setErr]     = useState('');

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const prefijos = prefStr.split(',').map(s => s.trim()).filter(Boolean);
      await apiFetch(`/permissions/categories/${encodeURIComponent(cat.nombre)}`, {
        method: 'PUT',
        body: JSON.stringify({ label, prefijos, permitido_default: defPerm }),
      });
      onSave();
    } catch(e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--surface,#1e293b)', border:'1px solid var(--border,#374151)',
        borderRadius:12, padding:28, minWidth:420, maxWidth:500, width:'90%' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 style={{ margin:0, fontSize:16, color:'var(--text,#f1f5f9)' }}>Editar categoría</h3>
            <div style={{ fontSize:12, color:'var(--muted,#94a3b8)', marginTop:2 }}>
              Clave: <code style={{ color:'var(--accent,#38bdf8)' }}>{cat.nombre}</code>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted,#94a3b8)', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {/* Etiqueta */}
          <div>
            <label style={{ fontSize:11, color:'var(--muted,#94a3b8)', display:'block', marginBottom:5,
              textTransform:'uppercase', letterSpacing:'.5px' }}>Etiqueta</label>
            <input value={label} onChange={e => setLabel(e.target.value)}
              placeholder="Ej: Locales"
              style={{ width:'100%', padding:'8px 12px', borderRadius:8,
                border:'1px solid var(--border,#374151)', background:'var(--bg,#0f172a)',
                color:'var(--text,#f1f5f9)', fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>

          {/* Prefijos */}
          <div>
            <label style={{ fontSize:11, color:'var(--muted,#94a3b8)', display:'block', marginBottom:5,
              textTransform:'uppercase', letterSpacing:'.5px' }}>Prefijos (separados por coma)</label>
            <input value={prefStr} onChange={e => setPrefStr(e.target.value)}
              placeholder="ej: 02, 04, 05, 06, 07"
              style={{ width:'100%', padding:'8px 12px', borderRadius:8,
                border:'1px solid var(--border,#374151)', background:'var(--bg,#0f172a)',
                color:'var(--text,#f1f5f9)', fontSize:13, outline:'none', boxSizing:'border-box',
                fontFamily:'monospace' }} />
            <div style={{ fontSize:10, color:'var(--muted,#94a3b8)', marginTop:4 }}>
              El AGI los usa para clasificar el destino de la llamada y aplicar el permiso correcto.
            </div>
          </div>

          {/* Permitido por defecto */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'10px 14px', background:'var(--bg,#0f172a)', borderRadius:8,
            border:'1px solid var(--border,#374151)' }}>
            <div>
              <div style={{ fontSize:13, color:'var(--text,#f1f5f9)', fontWeight:500 }}>Permitido por defecto</div>
              <div style={{ fontSize:11, color:'var(--muted,#94a3b8)', marginTop:2 }}>
                Para ANIs sin permiso explícito configurado
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11, fontWeight:600,
                color: defPerm ? '#22c55e' : '#ef4444' }}>
                {defPerm ? 'Sí' : 'No'}
              </span>
              <Toggle checked={defPerm} onChange={setDefPerm} />
            </div>
          </div>
        </div>

        {err && <div style={{ marginTop:12, color:'#ef4444', fontSize:12 }}>{err}</div>}

        <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'8px 18px', borderRadius:8,
            border:'1px solid var(--border,#374151)', background:'none',
            color:'var(--muted,#94a3b8)', cursor:'pointer', fontSize:13 }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'8px 18px', borderRadius:8,
            border:'none', background:'var(--accent,#38bdf8)', color:'#0f172a',
            fontWeight:600, cursor:saving?'not-allowed':'pointer', fontSize:13, opacity:saving?.7:1 }}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal para crear categoría nueva ────────────────────────────────────────
function NewCategoryModal({ onSave, onClose }) {
  const [nombre,  setNombre]  = useState('');
  const [label,   setLabel]   = useState('');
  const [prefStr, setPrefStr] = useState('');
  const [defPerm, setDefPerm] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  async function handleSave() {
    if (!nombre.trim() || !label.trim()) { setErr('Clave y etiqueta son requeridas'); return; }
    setSaving(true); setErr('');
    try {
      const prefijos = prefStr.split(',').map(s => s.trim()).filter(Boolean);
      await apiFetch('/permissions/categories', {
        method: 'POST',
        body: JSON.stringify({ nombre: nombre.trim(), label: label.trim(), prefijos, permitido_default: defPerm }),
      });
      onSave();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  const inputStyle = { width:'100%', padding:'8px 12px', borderRadius:8,
    border:'1px solid var(--border,#374151)', background:'var(--bg,#0f172a)',
    color:'var(--text,#f1f5f9)', fontSize:13, outline:'none', boxSizing:'border-box' };
  const labelStyle = { fontSize:11, color:'var(--muted,#94a3b8)', display:'block', marginBottom:5,
    textTransform:'uppercase', letterSpacing:'.5px' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--surface,#1e293b)', border:'1px solid var(--border,#374151)',
        borderRadius:12, padding:28, minWidth:420, maxWidth:500, width:'90%' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:16, color:'var(--text,#f1f5f9)' }}>Nueva categoría de marcado</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted,#94a3b8)', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={labelStyle}>Clave (sin espacios)</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="ej: celular"
              style={{ ...inputStyle, fontFamily:'monospace' }} />
            <div style={{ fontSize:10, color:'var(--muted,#94a3b8)', marginTop:4 }}>
              Identificador interno (se normaliza a minúsculas con guión bajo).
            </div>
          </div>
          <div>
            <label style={labelStyle}>Etiqueta</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="ej: Celular"
              style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Prefijos (separados por coma)</label>
            <input value={prefStr} onChange={e => setPrefStr(e.target.value)} placeholder="ej: 09"
              style={{ ...inputStyle, fontFamily:'monospace' }} />
            <div style={{ fontSize:10, color:'var(--muted,#94a3b8)', marginTop:4 }}>
              El AGI los usa para clasificar el destino de la llamada.
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'10px 14px', background:'var(--bg,#0f172a)', borderRadius:8,
            border:'1px solid var(--border,#374151)' }}>
            <div>
              <div style={{ fontSize:13, color:'var(--text,#f1f5f9)', fontWeight:500 }}>Permitido por defecto</div>
              <div style={{ fontSize:11, color:'var(--muted,#94a3b8)', marginTop:2 }}>
                Para ANIs sin permiso explícito configurado
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11, fontWeight:600, color: defPerm ? '#22c55e' : '#ef4444' }}>
                {defPerm ? 'Sí' : 'No'}
              </span>
              <Toggle checked={defPerm} onChange={setDefPerm} />
            </div>
          </div>
        </div>

        {err && <div style={{ marginTop:12, color:'#ef4444', fontSize:12 }}>{err}</div>}

        <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'8px 18px', borderRadius:8,
            border:'1px solid var(--border,#374151)', background:'none',
            color:'var(--muted,#94a3b8)', cursor:'pointer', fontSize:13 }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'8px 18px', borderRadius:8,
            border:'none', background:'var(--accent,#38bdf8)', color:'#0f172a',
            fontWeight:600, cursor:saving?'not-allowed':'pointer', fontSize:13, opacity:saving?.7:1 }}>
            {saving ? 'Creando…' : 'Crear categoría'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal para agregar nuevo ANI ─────────────────────────────────────────────
function AddANIModal({ onSave, onClose }) {
  const [ani, setAni] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleAdd() {
    const clean = ani.trim();
    if (!clean) { setErr("Ingresa un ANI/DID válido"); return; }
    setSaving(true);
    setErr("");
    try {
      await apiFetch(`/permissions/ani/${encodeURIComponent(clean)}/init`, { method: "POST" });
      onSave(clean);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center"
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background:"var(--surface,#1e293b)", border:"1px solid var(--border,#374151)",
        borderRadius:12, padding:28, minWidth:360, maxWidth:440, width:"90%",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:16, color:"var(--text,#f1f5f9)" }}>Agregar ANI / DID</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--muted,#94a3b8)", fontSize:20, cursor:"pointer" }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:12, color:"var(--muted,#94a3b8)", display:"block", marginBottom:6 }}>
            Número ANI / DID
          </label>
          <input
            value={ani}
            onChange={e => setAni(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="ej: 593984123456"
            style={{
              width:"100%", padding:"9px 12px", borderRadius:8,
              border:"1px solid var(--border,#374151)", background:"var(--bg,#0f172a)",
              color:"var(--text,#f1f5f9)", fontSize:14, outline:"none", boxSizing:"border-box"
            }}
            autoFocus
          />
          <div style={{ fontSize:11, color:"var(--muted,#94a3b8)", marginTop:4 }}>
            Se crearán los permisos por defecto según las categorías activas
          </div>
        </div>

        {err && <div style={{ color:"#ef4444", fontSize:12, marginBottom:10 }}>{err}</div>}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{
            padding:"8px 18px", borderRadius:8, border:"1px solid var(--border,#374151)",
            background:"none", color:"var(--muted,#94a3b8)", cursor:"pointer", fontSize:13
          }}>Cancelar</button>
          <button onClick={handleAdd} disabled={saving} style={{
            padding:"8px 18px", borderRadius:8, border:"none",
            background:"var(--accent,#38bdf8)", color:"#0f172a", fontWeight:600,
            cursor: saving ? "not-allowed" : "pointer", fontSize:13, opacity: saving ? .7 : 1
          }}>
            {saving ? "Creando…" : "Crear permisos"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Error boundary para evitar pantalla negra ────────────────────────────────
class PermErrorBoundary extends (require("react").Component) {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) return (
      <div style={{ padding:32, color:"#ef4444", fontFamily:"monospace", fontSize:13 }}>
        <strong>Error al renderizar Permisos:</strong><br/>
        {this.state.err.message}
        <br/><br/>
        <button onClick={() => this.setState({ err: null })}
          style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #ef4444",
            background:"none", color:"#ef4444", cursor:"pointer" }}>
          Reintentar
        </button>
      </div>
    );
    return this.props.children;
  }
}

// ─── Componente principal ─────────────────────────────────────────────────────
function PermissionsInner() {
  const [data, setData]           = useState(null);   // { categories, data }
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [editingAni, setEditingAni] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [search, setSearch]       = useState("");
  // window.confirm no abre cartel en el navegador del panel y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/permissions/all");
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = data?.categories || [];
  const aniList    = (data?.data || []).filter(row => {
    const s = search.trim().toLowerCase();
    return !s || row.ani.includes(s) || (row.cliente || "").toLowerCase().includes(s);
  });

  // Función para eliminar permisos (reset a defaults)
  async function handleReset(ani) {
    if (!(await confirmar(`¿Resetear todos los permisos de ${ani} a valores por defecto?`))) return;
    try {
      await apiFetch(`/permissions/ani/${encodeURIComponent(ani)}`, { method: "DELETE" });
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  // Color de celda según permiso
  function cellStyle(permitido) {
    return {
      padding:"8px 12px",
      textAlign:"center",
      background: permitido ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)",
    };
  }

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100 }}>
      {dialogoConfirm}
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, color:"var(--text,#f1f5f9)" }}>Permisos ANI / DID</h2>
          <p style={{ margin:"6px 0 0", fontSize:13, color:"var(--muted,#94a3b8)" }}>
            Control de marcado saliente por tipo de llamada — por ANI registrado
          </p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => setShowNewCat(true)} style={{
            padding:"9px 16px", borderRadius:8, border:"1px solid var(--accent,#38bdf8)",
            background:"transparent", color:"var(--accent,#38bdf8)", fontWeight:600,
            cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6
          }}>
            <span style={{ fontSize:16, lineHeight:1 }}>+</span> Nueva categoría
          </button>
          <button onClick={() => setShowAdd(true)} style={{
            padding:"9px 18px", borderRadius:8, border:"none",
            background:"var(--accent,#38bdf8)", color:"#0f172a", fontWeight:600,
            cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6
          }}>
            <span style={{ fontSize:16, lineHeight:1 }}>+</span> Agregar ANI
          </button>
        </div>
      </div>

      {/* Buscador */}
      <div style={{ marginBottom:16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar ANI…"
          style={{
            padding:"8px 12px", borderRadius:8, border:"1px solid var(--border,#374151)",
            background:"var(--surface,#1e293b)", color:"var(--text,#f1f5f9)",
            fontSize:13, outline:"none", width:220
          }}
        />
      </div>

      {/* Categorías leyenda con edición */}
      {categories.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, color:"var(--muted,#94a3b8)", textTransform:"uppercase",
            letterSpacing:".5px", fontWeight:600, marginBottom:8 }}>
            Categorías de marcado
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {categories.map(c => (
              <div key={c.nombre} style={{
                display:"flex", alignItems:"center", gap:6,
                padding:"5px 8px 5px 12px", borderRadius:8,
                background:"var(--surface,#1e293b)", border:"1px solid var(--border,#374151)",
              }}>
                <div style={{ fontSize:12 }}>
                  <span style={{ color:"var(--text,#f1f5f9)", fontWeight:600 }}>{c.label}</span>
                  <span style={{ color:"var(--muted,#94a3b8)" }}> · {parsePrefijos(c.prefijos).join(", ")}</span>
                  <span style={{ marginLeft:6, fontSize:10, padding:"1px 5px", borderRadius:3,
                    background: c.permitido_default ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)",
                    color: c.permitido_default ? "#22c55e" : "#ef4444" }}>
                    {c.permitido_default ? "✓ def" : "✕ def"}
                  </span>
                </div>
                <button
                  onClick={() => setEditingCat(c)}
                  title="Editar categoría"
                  style={{ background:"none", border:"none", color:"var(--muted,#94a3b8)",
                    cursor:"pointer", fontSize:12, padding:"3px 5px", borderRadius:4,
                    lineHeight:1, opacity:.7 }}>
                  ✏
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estados */}
      {loading && (
        <div style={{ color:"var(--muted,#94a3b8)", fontSize:14, padding:32, textAlign:"center" }}>
          Cargando permisos…
        </div>
      )}
      {error && (
        <div style={{
          background:"rgba(239,68,68,.1)", border:"1px solid rgba(239,68,68,.3)",
          borderRadius:8, padding:"12px 16px", color:"#ef4444", fontSize:13, marginBottom:16
        }}>
          {error}
        </div>
      )}

      {/* Tabla */}
      {!loading && !error && (
        <div style={{ overflowX:"auto" }}>
          <table style={{
            width:"100%", borderCollapse:"collapse",
            fontSize:13, color:"var(--text,#f1f5f9)",
          }}>
            <thead>
              <tr style={{ borderBottom:"2px solid var(--border,#374151)" }}>
                <th style={{ textAlign:"left", padding:"10px 14px", color:"var(--muted,#94a3b8)", fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:".5px" }}>
                  ANI / DID
                </th>
                {categories.map(c => (
                  <th key={c.nombre} style={{ padding:"10px 12px", color:"var(--muted,#94a3b8)", fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:".5px", textAlign:"center" }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ padding:"10px 12px", color:"var(--muted,#94a3b8)", fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:".5px", textAlign:"center" }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {aniList.length === 0 && (
                <tr>
                  <td colSpan={categories.length + 2} style={{ padding:"32px 14px", textAlign:"center", color:"var(--muted,#94a3b8)" }}>
                    {search ? "No se encontraron ANIs que coincidan" : "No hay DIDs de clientes activos ni ANIs configurados todavía"}
                  </td>
                </tr>
              )}
              {aniList.map((row, idx) => (
                <tr key={row.ani} style={{
                  borderBottom:"1px solid var(--border,#374151)",
                  background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,.02)",
                }}>
                  <td style={{ padding:"10px 14px" }}>
                    <code style={{ fontSize:13, color:"var(--accent,#38bdf8)" }}>{row.ani}</code>
                    {row.cliente && (
                      <div style={{ fontSize:11, color:"var(--muted,#94a3b8)", marginTop:2 }}>{row.cliente}</div>
                    )}
                    {!row.configurado && (
                      <span style={{ display:"inline-block", marginTop:3, fontSize:9, padding:"1px 6px",
                        borderRadius:4, background:"rgba(148,163,184,.15)", color:"var(--muted,#94a3b8)",
                        textTransform:"uppercase", letterSpacing:".5px" }}>
                        por defecto
                      </span>
                    )}
                  </td>
                  {categories.map(c => {
                    const p = row.permisos[c.nombre];
                    const permitido = p?.permitido ?? true;
                    return (
                      <td key={c.nombre} style={cellStyle(permitido)}>
                        <span style={{
                          display:"inline-block", width:20, height:20, borderRadius:"50%",
                          background: permitido ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)",
                          border: `2px solid ${permitido ? "#22c55e" : "#ef4444"}`,
                          fontSize:11, lineHeight:"16px", textAlign:"center", color: permitido ? "#22c55e" : "#ef4444"
                        }}>
                          {permitido ? "✓" : "✕"}
                        </span>
                      </td>
                    );
                  })}
                  <td style={{ padding:"8px 12px", textAlign:"center" }}>
                    <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                      <button
                        onClick={() => setEditingAni(row)}
                        style={{
                          padding:"5px 12px", borderRadius:6, border:"1px solid var(--accent,#38bdf8)",
                          background:"transparent", color:"var(--accent,#38bdf8)", cursor:"pointer", fontSize:11, fontWeight:500
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleReset(row.ani)}
                        style={{
                          padding:"5px 12px", borderRadius:6, border:"1px solid var(--border,#374151)",
                          background:"transparent", color:"var(--muted,#94a3b8)", cursor:"pointer", fontSize:11
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal edición */}
      {editingAni && (
        <EditModal
          ani={editingAni.ani}
          permisos={categories.map(c => ({
            categoria: c.nombre,
            permitido: editingAni.permisos[c.nombre]?.permitido ?? true,
          }))}
          categories={categories}
          onSave={() => { setEditingAni(null); load(); }}
          onClose={() => setEditingAni(null)}
        />
      )}

      {/* Modal agregar */}
      {showAdd && (
        <AddANIModal
          onSave={() => { setShowAdd(false); load(); }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Modal nueva categoría */}
      {showNewCat && (
        <NewCategoryModal
          onSave={() => { setShowNewCat(false); load(); }}
          onClose={() => setShowNewCat(false)}
        />
      )}

      {/* Modal editar categoría */}
      {editingCat && (
        <EditCategoryModal
          cat={editingCat}
          onSave={() => { setEditingCat(null); load(); }}
          onClose={() => setEditingCat(null)}
        />
      )}
    </div>
  );
}

export default function Permissions() {
  return (
    <PermErrorBoundary>
      <PermissionsInner />
    </PermErrorBoundary>
  );
}
